<template>
  <div :class="!isModal ? 'profile-page-container' : ''">
    <PremiumPage v-if="!isModal" title="Society Profile" subtitle="Manage your society details and social media presence">
      <div class="pa-3 pa-md-6">
        <v-row :no-gutters="$vuetify.breakpoint.smAndDown">
          <v-col cols="12" md="7">
            <div class="premium-panel h-100">
              <div class="premium-panel-header">
                <div>
                  <div class="premium-panel-title">Profile Settings</div>
                  <div class="premium-panel-sub">Update contact and address information</div>
                </div>
              </div>
              <div class="premium-panel-body">
            <v-form ref="profileFormPage" v-if="dataLoaded">
              <v-container fluid class="pa-0">
              <v-row>
                <v-col cols="12" class="d-flex justify-center mb-6">
                  <input
                    id="profile_pic_page_input"
                    type="file"
                    ref="profile_pic_page"
                    @change="changeFile($event)"
                    accept="image/*"
                    style="position:absolute;opacity:0;width:0;height:0;overflow:hidden;"
                  />
                  <div class="avatar-uploader-container">
                    <v-avatar
                      :size="$vuetify.breakpoint.xs ? 90 : 140"
                      class="elevation-4 border-soft"
                      :style="{ background: primaryColor + '20' }"
                    >
                      <img
                        v-if="previewImage || user.logo_url"
                        :src="
                          previewImage || API_BASE_URL + '/uploads/' + user.logo_url
                        "
                        style="object-fit: contain"
                      />
                      <span
                        v-else
                        :class="$vuetify.breakpoint.xs ? 'text-h3 font-weight-bold' : 'text-h2 font-weight-bold'"
                        :style="{ color: primaryColor }"
                        >{{
                          user.name ? user.name.charAt(0).toUpperCase() : "A"
                        }}</span
                      >
                    </v-avatar>
                    <v-btn
                      fab
                      small
                      depressed
                      @click="openFileExplorer"
                      class="avatar-edit-btn"
                      color="var(--brand)"
                    >
                      <v-icon small color="white">mdi-pencil</v-icon>
                    </v-btn>
                  </div>
                </v-col>
              </v-row>

              <v-row dense>
                    <v-col cols="12">
                      <PremiumInput
                        v-model="user.name"
                        label="Society Name"
                        placeholder="Enter society name"
                        icon="mdi-office-building-outline"
                        v-validate="'required'"
                        name="name"
                        :error="errors.first('name') || serverSideErrors.name"
                      />
                    </v-col>
                    <v-col cols="12">
                      <PremiumInput
                        v-model="user.contact_person_name"
                        label="Contact Person"
                        icon="mdi-account-outline"
                        v-validate="'required'"
                        name="contact_person_name"
                        :error="errors.first('contact_person_name') || serverSideErrors.contact_person_name"
                      />
                    </v-col>
                    <v-col cols="12">
                      <div class="premium-field-label mb-2">Mobile Number</div>
                      <div class="phone-row">
                        <div class="country-code-wrap mr-2">
                          <vue-country-code
                            @onSelect="onSelect"
                            class="custom-country-code-select-full"
                            :defaultCountry="user.countryIso || 'IN'"
                          />
                        </div>
                        <PremiumInput
                          v-model="user.contact_person_number"
                          label=""
                          style="flex: 1"
                          v-validate="'required'"
                          name="number"
                          :error="errors.first('number') || serverSideErrors.contact_person_number"
                        />
                      </div>
                    </v-col>
                    <v-col cols="12">
                      <PremiumInput
                        v-model="user.contact_person_email"
                        label="Email Address"
                        icon="mdi-email-outline"
                        v-validate="'required|email'"
                        name="email"
                        :error="errors.first('email') || serverSideErrors.contact_person_email"
                      />
                    </v-col>
              </v-row>

              <v-row dense class="mt-4">
                <v-col cols="12">
                  <PremiumInput
                    v-model="user.address"
                    label="Society Address"
                    placeholder="Enter full address"
                    icon="mdi-map-marker-outline"
                    multiline
                    :rows="2"
                    v-validate="'required'"
                    name="address"
                    :error="errors.first('address') || serverSideErrors.address"
                  />
                </v-col>
                <v-col cols="12">
                  <PremiumInput
                    v-model="user.country"
                    label="Country *"
                    name="country"
                    placeholder="Select country"
                    is-select
                    searchable
                    :items="countries"
                    item-text="name"
                    item-value="id"
                    v-validate="'required'"
                    :error="errors.first('country') || serverSideErrors.country"
                    @change="onCountryChange"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <PremiumInput
                    v-model="user.state"
                    label="State *"
                    name="state"
                    placeholder="Select state"
                    is-select
                    searchable
                    :items="states"
                    item-text="name"
                    item-value="id"
                    :disabled="!user.country"
                    v-validate="'required'"
                    :error="errors.first('state') || serverSideErrors.state"
                    @change="onStateChange"
                  />
                </v-col>
                <v-col v-if="user.state === 'Other' || user.state === 'other'" cols="12" sm="6">
                  <PremiumInput
                    key="other-state-input"
                    v-model="user.other_state"
                    label="Specify State *"
                    name="otherState"
                    data-vv-name="otherState"
                    data-vv-as="state"
                    placeholder="Enter your state"
                    v-validate="'required'"
                    :error="errors.first('otherState') || serverSideErrors.otherState"
                  />
                </v-col>

                <v-col cols="12" sm="6">
                  <PremiumInput
                    v-model="user.city"
                    label="City *"
                    name="city"
                    :placeholder="(user.state === 'Other' || user.state === 'other') ? 'Enter city manually below' : 'Select city'"
                    is-select
                    searchable
                    :items="cities"
                    item-text="name"
                    item-value="id"
                    :disabled="!user.state || user.state === 'Other' || user.state === 'other'"
                    v-validate="'required'"
                    :error="errors.first('city') || serverSideErrors.city"
                  />
                </v-col>
                <v-col v-if="user.city === 'Other' || user.city === 'other'" cols="12" sm="6">
                  <PremiumInput
                    key="other-city-input"
                    v-model="user.other_city"
                    label="Specify City *"
                    name="otherCity"
                    data-vv-name="otherCity"
                    data-vv-as="city"
                    placeholder="Enter your city"
                    v-validate="'required'"
                    :error="errors.first('otherCity') || serverSideErrors.otherCity"
                  />
                </v-col>

                <v-col cols="12" class="mt-4">
                  <PremiumInput
                    v-model="user.current_balance"
                    label="Current Balance"
                    icon="mdi-currency-inr"
                    type="number"
                  />
                </v-col>
              </v-row>
              </v-container>
            </v-form>
              </div>
              <div class="premium-panel-footer pa-6 pt-0">
                <v-btn
                  depressed
                  class="btn-premium-primary"
                  @click="updateProfile"
                  :loading="loading"
                >
                  Submit
                </v-btn>
              </div>
            </div>
          </v-col>

          <v-col cols="12" md="5">
            <div class="premium-panel">
              <div class="premium-panel-header">
                <div>
                  <div class="premium-panel-title">Social Media Links</div>
                  <div class="premium-panel-sub">Manage external links and social presence</div>
                </div>
              </div>
              <div class="premium-panel-body">
            <v-form ref="socialForm">
              <v-container fluid class="pa-0">
              <v-row dense>
                <v-col cols="12">
                  <PremiumInput
                    v-model="user.facebook_url"
                    label="Facebook URL"
                    placeholder="https://facebook.com/yoursociety"
                    icon="mdi-facebook"
                    name="facebook_url"
                    v-validate="'url'"
                    :error="errors.first('facebook_url') || socialErrors.facebook_url"
                  />
                </v-col>
                <v-col cols="12">
                  <PremiumInput
                    v-model="user.instagram_url"
                    label="Instagram URL"
                    placeholder="https://instagram.com/yoursociety"
                    icon="mdi-instagram"
                    name="instagram_url"
                    v-validate="'url'"
                    :error="errors.first('instagram_url') || socialErrors.instagram_url"
                  />
                </v-col>
                <v-col cols="12">
                  <PremiumInput
                    v-model="user.twitter_url"
                    label="Twitter / X URL"
                    placeholder="https://twitter.com/yoursociety"
                    icon="mdi-twitter"
                    name="twitter_url"
                    v-validate="'url'"
                    :error="errors.first('twitter_url') || socialErrors.twitter_url"
                  />
                </v-col>
                <v-col cols="12">
                  <PremiumInput
                    v-model="user.linkedin_url"
                    label="LinkedIn URL"
                    placeholder="https://linkedin.com/company/yoursociety"
                    icon="mdi-linkedin"
                    name="linkedin_url"
                    v-validate="'url'"
                    :error="errors.first('linkedin_url') || socialErrors.linkedin_url"
                  />
                </v-col>
                <v-col cols="12">
                  <PremiumInput
                    v-model="user.youtube_url"
                    label="YouTube URL"
                    placeholder="https://youtube.com/c/yoursociety"
                    icon="mdi-youtube"
                    name="youtube_url"
                    v-validate="'url'"
                    :error="errors.first('youtube_url') || socialErrors.youtube_url"
                  />
                </v-col>
              </v-row>
              </v-container>
            </v-form>
              </div>
              <div class="premium-panel-footer pa-6 pt-0">
                <v-btn
                  depressed
                  class="btn-premium-primary"
                  @click="updateSocialMediaLinks"
                  :loading="loadingSocialMedia"
                >
                  Submit
                </v-btn>
              </div>
            </div>
          </v-col>
        </v-row>
      </div>
    </PremiumPage>

    <div v-else>
      <input
        id="profile_pic_modal_input"
        type="file"
        @change="changeFile($event)"
        ref="profile_pic"
        style="display: none;"
        accept="image/*"
      />
      <v-form ref="profileForm" v-if="dataLoaded">
        <v-row>
          <v-col cols="12" class="d-flex justify-center mb-8">
            <div class="avatar-uploader-container">
              <v-avatar
                :size="$vuetify.breakpoint.xs ? 90 : 140"
                class="elevation-3 avatar-border"
                :style="{
                  background: primaryColor + '20',
                }"
              >
                <img
                  v-if="previewImage || user.logo_url"
                  :src="
                    previewImage || API_BASE_URL + '/uploads/' + user.logo_url
                  "
                  style="object-fit: contain"
                />
                <span
                  v-else
                  :class="$vuetify.breakpoint.xs ? 'text-h3 font-weight-bold' : 'text-h2 font-weight-bold'"
                  :style="{ color: primaryColor }"
                  >{{
                    user.name ? user.name.charAt(0).toUpperCase() : "A"
                  }}</span
                >
              </v-avatar>
              <v-btn
                fab
                small
                depressed
                @click="openFileExplorer"
                class="avatar-edit-btn"
              >
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
            </div>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col cols="12">
            <PremiumInput
              v-model="user.name"
              label="Society Name"
              icon="mdi-office-building-outline"
              v-validate="'required'"
              name="name"
              :error="errors.first('name') || serverSideErrors.name"
            />
          </v-col>
          <v-col cols="12">
            <PremiumInput
              v-model="user.contact_person_name"
              label="Contact Person"
              icon="mdi-account-outline"
              v-validate="'required'"
              name="contact_person_name"
              :error="errors.first('contact_person_name') || serverSideErrors.contact_person_name"
            />
          </v-col>
          <v-col cols="12">
            <div class="premium-field-label mb-2">Mobile Number</div>
            <div class="phone-row">
              <div class="country-code-wrap">
                <vue-country-code
                  @onSelect="onSelect"
                  class="custom-country-code-select-full"
                  :defaultCountry="user.countryIso || 'IN'"
                />
              </div>
              <PremiumInput
                v-model="user.contact_person_number"
                label=""
                style="flex: 1"
                v-validate="'required'"
                name="number"
                :error="errors.first('number') || serverSideErrors.contact_person_number"
              />
            </div>
          </v-col>
          <v-col cols="12">
            <PremiumInput
              v-model="user.contact_person_email"
              label="Email Address"
              icon="mdi-email-outline"
              v-validate="'required|email'"
              name="email"
              :error="errors.first('email') || serverSideErrors.contact_person_email"
            />
          </v-col>
        </v-row>

        <v-row dense class="mt-4">
          <v-col cols="12">
            <PremiumInput
              v-model="user.address"
              label="Society Address"
              placeholder="Enter full address"
              icon="mdi-map-marker-outline"
              multiline
              :rows="2"
              v-validate="'required'"
              name="address"
              :error="errors.first('address') || serverSideErrors.address"
            />
          </v-col>
          <v-col cols="12">
            <PremiumInput
              v-model="user.country"
              label="Country *"
              name="country"
              placeholder="Select country"
              is-select
              searchable
              :items="countries"
              item-text="name"
              item-value="id"
              v-validate="'required'"
              :error="errors.first('country') || serverSideErrors.country"
              @change="onCountryChange"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <PremiumInput
              v-model="user.state"
              label="State *"
              name="state"
              placeholder="Select state"
              is-select
              searchable
              :items="states"
              item-text="name"
              item-value="id"
              :disabled="!user.country"
              v-validate="'required'"
              :error="errors.first('state') || serverSideErrors.state"
              @change="onStateChange"
            />
          </v-col>
          <v-col v-if="user.state === 'Other' || user.state === 'other'" cols="12" sm="6">
            <PremiumInput
              key="other-state-input"
              v-model="user.other_state"
              label="Specify State *"
              name="otherState"
              data-vv-name="otherState"
              data-vv-as="state"
              placeholder="Enter your state"
              v-validate="'required'"
              :error="errors.first('otherState') || serverSideErrors.otherState"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <PremiumInput
              v-model="user.city"
              label="City *"
              name="city"
              :placeholder="(user.state === 'Other' || user.state === 'other') ? 'Enter city manually below' : 'Select city'"
              is-select
              searchable
              :items="cities"
              item-text="name"
              item-value="id"
              :disabled="!user.state || user.state === 'Other' || user.state === 'other'"
              v-validate="'required'"
              :error="errors.first('city') || serverSideErrors.city"
            />
          </v-col>
          <v-col v-if="user.city === 'Other' || user.city === 'other'" cols="12" sm="6">
            <PremiumInput
              key="other-city-input"
              v-model="user.other_city"
              label="Specify City *"
              name="otherCity"
              data-vv-name="otherCity"
              data-vv-as="city"
              placeholder="Enter your city"
              v-validate="'required'"
              :error="errors.first('otherCity') || serverSideErrors.otherCity"
            />
          </v-col>

          <v-col cols="12" class="mt-4">
            <PremiumInput
              v-model="user.current_balance"
              label="Current Balance"
              icon="mdi-currency-inr"
              type="number"
            />
          </v-col>
        </v-row>
      </v-form>
    </div>

    <ImageCropper
      v-model="cropDialog"
      :image-src="cropSrc"
      title="Crop Society Logo"
      shape="square"
      :output-size="400"
      @cropped="onCropped"
    />
  </div>
</template>

<script>
import { mapMutations, mapState } from "vuex";
import PremiumInput from "../common/PremiumInput.vue";
import ImageCropper from "../common/ImageCropper.vue";
import PremiumPage from "../common/PremiumPage.vue";

export default {
  name: "SocietyAdminProfile",

  components: { PremiumInput, ImageCropper, PremiumPage },

  props: {
    isModal: { type: Boolean, default: false },
  },

  data() {
    return {
      loading: false,
      cropDialog: false,
      cropSrc: "",
      user: {
        name: "",
        address: "",
        contact_person_name: "",
        contact_person_number: "",
        contact_person_email: "",
        logo_url: "",
        current_balance: 0,
        country_code: "",
        countryIso: "",
        country: "",
        state: "",
        city: "",
        other_state: "",
        other_city: "",
        facebook_url: "",
        instagram_url: "",
        twitter_url: "",
        linkedin_url: "",
        youtube_url: "",
      },
      serverSideErrors: {},
      profile_pic: null,
      previewImage: null,
      dataLoaded: false,
      countries: [],
      states: [],
      cities: [],
      loadingSocialMedia: false,
      socialErrors: {},
    };
  },
  created() {
    this.getSocietyDetail();
    this.getCountries();
  },

  computed: {
    ...mapState("theme", ["primaryColor"]),
  },

  watch: {
    loading(val) {
      this.$emit("loading", val);
    },
  },

  methods: {
    getSocietyDetail() {
      let _self = this;
      _self.$axios.get("/societyadmin/societyProfile").then((response) => {
        let data = response.data;

        if (data.status) {
          _self.user = data.society;

          _self.user.countryIso = data.society.countryIso;
          localStorage.setItem("flag", JSON.stringify(_self.user.countryIso));

          if (_self.user.other_state) {
            _self.user.state = 'Other';
          }
          if (_self.user.other_city) {
            _self.user.city = 'Other';
          }

          if (_self.user.country) {
            _self.getStates(_self.user.country).then(() => {
              if (_self.user.state && _self.user.state !== 'Other' && _self.user.state !== 'other') {
                _self.getCities(_self.user.state);
              } else if (_self.user.state === 'Other' || _self.user.state === 'other') {
                _self.cities = [{ id: 'Other', name: 'Other' }];
              }
            });
          }
        } else {
          if (data.errors) {
            _self.serverSideErrors = {};

            for (let i = 0; i < data.errors.length; i++) {
              _self.serverSideErrors[data.errors[i].field] =
                data.errors[i].message;
            }
          } else {
            this.setAlert({
              showAlert: true,
              variant: "red accent-3",
              timeout: "2000",
              message: data.message,
            });
          }
        }
        _self.dataLoaded = true;
      }).catch(() => {
        _self.dataLoaded = true;
      });
    },
    getCountries() {
      this.$axios.get('society/countries').then(res => {
        if (res.data.status) {
          this.countries = res.data.data.map(item => ({ ...item, id: item.id || item._id }));
        }
      });
    },
    getStates(country_id) {
      this.states = [];
      this.cities = [];
      if (!country_id) return Promise.resolve();
      return this.$axios.get('society/states', { params: { country_id } }).then(res => {
        if (res.data.status) {
          this.states = res.data.data.map(item => ({ ...item, id: item.id || item._id }));
          this.states.push({ id: 'Other', name: 'Other' });
        }
      }).catch(() => {
        this.states = [];
        this.cities = [];
      });
    },
    getCities(state_id) {
      this.cities = [];
      if (!state_id || state_id === 'Other' || state_id === 'other') {
        this.cities = [{ id: 'Other', name: 'Other' }];
        return Promise.resolve();
      }
      return this.$axios.get('society/cities', { params: { state_id } }).then(res => {
        if (res.data.status) {
          this.cities = res.data.data.map(item => ({ ...item, id: item.id || item._id }));
          this.cities.push({ id: 'Other', name: 'Other' });
        }
      }).catch(() => {
        this.cities = [];
      });
    },
    onCountryChange(val) {
      this.user.state = '';
      this.user.other_state = '';
      this.user.city = '';
      this.user.other_city = '';
      this.getStates(val);
    },
    onStateChange(val) {
      if (val === 'Other' || val === 'other') {
        this.user.city = 'Other';
      } else {
        this.user.city = '';
        this.user.other_state = '';
      }
      this.user.other_city = '';
      this.getCities(val);
    },
    onSelect(event) {
      this.user.country_code = "+" + event.dialCode;
      this.user.countryIso = event.iso2;
    },
    ...mapMutations("alert", ["setAlert"]),
    updateProfile() {
      let _self = this;
      this.$validator.validateAll().then((result) => {
        if (result) {
          _self.loading = true;
          let formData = new FormData();

          formData.append("name", this.user.name);
          formData.append("address", this.user.address);
          formData.append("contact_person_name", this.user.contact_person_name);
          formData.append("current_balance", this.user.current_balance || 0);
          formData.append(
            "contact_person_number",
            this.user.contact_person_number,
          );
          formData.append(
            "contact_person_email",
            this.user.contact_person_email,
          );
          if (this.user.state === 'Other' || this.user.state === 'other') {
            formData.append("state", '0');
            formData.append("other_state", this.user.other_state);
          } else {
            formData.append("state", this.user.state || '');
            formData.append("other_state", '');
          }

          if (this.user.city === 'Other' || this.user.city === 'other') {
            formData.append("city", '0');
            formData.append("other_city", this.user.other_city);
          } else {
            formData.append("city", this.user.city || '');
            formData.append("other_city", '');
          }

          formData.append("country", this.user.country || '');
          formData.append("country_code", this.user.country_code);
          formData.append("countryIso", this.user.countryIso);
          formData.append("logo_url", this.profile_pic);

          _self.$axios
            .post("/societyadmin/societyProfile", formData, {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            })
            .then((response) => {
              let data = response.data;

              _self.loading = false;
              _self.serverSideErrors = {};

              if (data.status) {
                let currentToken = this.user.token;
                this.user = data.user;
                this.profile_pic = {};

                if (this.user.other_state) {
                  this.user.state = 'Other';
                }
                if (this.user.other_city) {
                  this.user.city = 'Other';
                }

                if (this.user.country) {
                  this.getStates(this.user.country).then(() => {
                    if (this.user.state && this.user.state !== 'Other' && this.user.state !== 'other') {
                      this.getCities(this.user.state);
                    } else if (this.user.state === 'Other' || this.user.state === 'other') {
                      this.cities = [{ id: 'Other', name: 'Other' }];
                    }
                  });
                }

                data.user.role = "societyadmin";
                data.user.token = currentToken || data.user.token;
                localStorage.setItem("user", JSON.stringify(data.user));

                if (data.user.logo_url) {
                  _self.$emit("changeProfilePic");
                }

                this.setAlert({
                  showAlert: true,
                  message: data.message,
                  variant: "green accent-4",
                  timeout: 2000,
                });
                if (this.isModal) {
                  this.$emit("close");
                }
              } else {
                if (!data.errors) {
                  this.setAlert({
                    showAlert: true,
                    message: data.message,
                    variant: "red accent-3",
                    timeout: 2000,
                  });
                } else {
                  for (let i = 0; i < data.errors.length; i++) {
                    this.serverSideErrors[data.errors[i].field] =
                      data.errors[i].message;
                  }
                }
              }
            });
        }
      });
    },
    updateSocialMediaLinks() {
      this.socialErrors = {};

      this.$validator.validateAll().then(valid => {
        if (!valid) return;

        this.loadingSocialMedia = true;
        const data = {
          facebook_url:  this.user.facebook_url  || '',
          instagram_url: this.user.instagram_url || '',
          twitter_url:   this.user.twitter_url   || '',
          linkedin_url:  this.user.linkedin_url  || '',
          youtube_url:   this.user.youtube_url   || '',
        };
        this.$axios.post("/societyadmin/societySocialMedia", data).then((res) => {
          this.loadingSocialMedia = false;
          if (res.data.status) {
            this.setAlert({
              showAlert: true,
              message: res.data.message,
              variant: "green accent-4",
              timeout: 2000,
            });
            const localUser = JSON.parse(localStorage.getItem("user") || "{}");
            Object.assign(localUser, data);
            localStorage.setItem("user", JSON.stringify(localUser));
          } else {
            if (res.data.errors && res.data.errors.length) {
              const errs = {};
              res.data.errors.forEach(e => { errs[e.field] = e.message; });
              this.socialErrors = errs;
            } else {
              this.setAlert({
                showAlert: true,
                message: res.data.message,
                variant: "red accent-3",
                timeout: 2000,
              });
            }
          }
        }).catch(() => {
          this.loadingSocialMedia = false;
        });
      });
    },
    openFileExplorer() {
      this.$nextTick(() => {
        const inputId = this.isModal ? 'profile_pic_modal_input' : 'profile_pic_page_input';
        const fileInput = document.getElementById(inputId);
        
        if (fileInput) {
          fileInput.click();
        } else {
          const fallback = this.$el.querySelector('input[type="file"]');
          if (fallback) fallback.click();
        }
      });
    },

    changeFile(e) {
      const file = e.target.files[0];
      if (!file) return;
      e.target.value = "";
      const reader = new FileReader();
      reader.onload = (ev) => {
        this.cropSrc = ev.target.result;
        this.cropDialog = true;
      };
      reader.readAsDataURL(file);
    },

    onCropped({ file, previewUrl }) {
      this.profile_pic  = file;
      this.previewImage = previewUrl;
    },
  },
};
</script>
<style scoped>
.profile-page-container {
  padding: 24px;
}

@media (max-width: 768px) {
  .profile-page-container {
    padding: 8px;
  }
  .premium-panel-header {
    padding: 12px 16px !important;
  }
  .premium-panel-body {
    padding: 12px !important;
  }
}

.premium-panel {
  background: var(--surface, #fff);
  border: 1px solid var(--border, #e5e7eb);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.02);
  /* height: 100%; */
  display: flex;
  flex-direction: column;
}

.premium-panel-header {
  padding: 16px 20px;
  border-bottom: 1px solid var(--border, #e5e7eb);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.premium-panel-title {
  font-family: "Bricolage Grotesque", sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: var(--tx, #111827);
  letter-spacing: -0.01em;
}

.premium-panel-sub {
  font-size: 12px;
  color: var(--tx2, #6b7280);
  margin-top: 2px;
}

.premium-panel-body {
  padding: 20px;
  flex: 1;
}

.premium-panel-footer {
  /* padding is added via vuetify classes pa-6 pt-0 */
}
</style>

<style>
.ism-cropper-dialog {
  border-radius: 16px !important;
  overflow: hidden !important;
  box-shadow: 0 24px 64px rgba(15, 23, 42, 0.32) !important;
}
</style>

<style scoped>
.font-30 {
  font-size: 30px !important;
}
.edit-icon {
  z-index: 1000;
}
.custom-country-code-select {
  width: 72px;
  height: 46px;
  border: 1px solid var(--border, rgba(0, 0, 0, 0.1)) !important;
  background-color: var(--surface2, #fafafa);
  border-radius: 8px !important;
  transition: all 0.2s ease;
}

.premium-country-wrap-full {
  width: 100%;
}
.custom-country-code-select-full {
  width: 100%;
  height: 40px;
  border: 1px solid var(--border, rgba(0, 0, 0, 0.1)) !important;
  background-color: var(--surface2) !important;
  border-radius: 8px !important;
  transition: all 0.2s ease;
}
::v-deep .custom-country-code-select-full .vue-country-select {
  width: 100%;
  height: 100%;
  border: none !important;
}
::v-deep .custom-country-code-select-full .dropdown-list {
  width: 100% !important;
}
::v-deep .theme--dark .custom-country-code-select-full,
.theme--dark .custom-country-code-select,
.custom-country-code-select-full.is-dark-dropdown,
.custom-country-code-select.is-dark-dropdown {
  background-color: rgba(255, 255, 255, 0.03) !important;
  border-color: rgba(255, 255, 255, 0.4) !important;
}

.custom-country-code-select-full.is-dark-dropdown .dropdown-arrow,
.custom-country-code-select.is-dark-dropdown .dropdown-arrow {
  border-color: #ffffff transparent transparent !important;
}

.theme--dark .avatar-edit-btn,
.theme--dark .avatar-border {
  border-color: var(--surface2, #1E1E1E) !important;
}
.theme--dark .avatar-edit-btn {
  background-color: black !important;
}

.custom-country-code-select:hover,
.custom-country-code-select-full:hover {
  border-color: var(--brand) !important;
}

.premium-country-wrap {
  display: flex;
  flex-direction: column;
}

.premium-label {
  display: block;
  font-size: 11.5px;
  font-weight: 700;
  color: var(--tx3, #6b7280);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
  padding-left: 4px;
}

.vue-country-select:focus-within {
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.12) !important;
  border-color: var(--brand) !important;
}

/* આ કન્ટેનર હવે પેજ અને મોડલ બંનેમાં બટનને પરફેક્ટ પ્લેસમેન્ટ આપશે */
.avatar-uploader-container {
  position: relative;
  display: inline-block;
}

/* .avatar-border {
  border: 2px solid white !important;
} */

.avatar-edit-btn {
  position: absolute !important;
  bottom: 2px !important;
  right: 2px !important;
  width: 38px !important;
  height: 38px !important;
  border: 2px solid white !important;
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.12) !important;
  transition: all 0.2s ease !important;
  background-color: rgb(229, 229, 229) !important;
}

.avatar-edit-btn .v-icon {
  color: #000 !important;
}

.theme--dark .avatar-edit-btn .v-icon {
  color: #fff !important;
}

.avatar-edit-btn:hover {
  transform: scale(1.1);
}

.theme--dark .avatar-edit-btn {
  border-color: #111827 !important;
}

.premium-field-label {
  font-size: 11.5px;
  font-weight: 700;
  color: var(--tx3, #6b7280);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding-left: 4px;
}

.phone-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.country-code-wrap {
  flex-shrink: 0;
  width: 90px;
}

::v-deep .custom-country-code-select-full .dropdown-list {
  width: 260px !important;
  top: auto !important;
  bottom: 100% !important;
  margin-bottom: 4px;
  max-height: 200px;
  overflow-y: auto;
}
</style>